import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaChevronLeft, FaChevronRight, FaCubes, FaShieldAlt, FaChartLine } from "react-icons/fa";

const slides = [
  {
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd",
    tagline: "VAMCO & ASSOCIATES • PREMIER ENGINEERING ",
    title: "High-Precision Structural Rebar Detailing",
    subtitle: "Producing precise reinforcement detailing that meets project specifications, utilizing code-compliant engineering layouts to accelerate site workflows.",
    metric: "0.00mm Precision"
  },
  {
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5",
    tagline: "OPTIMIZED BAR BENDING SCHEDULES (BBS)",
    title: "Minimize Steel Scrap & Wastage",
    subtitle: "Developing accurate and highly optimized schedules designed explicitly to reduce project structural material costs and speed up site placement.",
    metric: "Zero Steel Waste Strategy"
  },
  {
    image: "https://images.unsplash.com/photo-1517048676732-d65bc937f952",
    tagline: "COMPREHENSIVE MEP BIM COORDINATION",
    title: "Fully Integrated, Clash-Free Models",
    subtitle: "Bridging the gap between design and construction by delivering coordinated structural, architectural, and mechanical engineering layouts.",
    metric: "Flawless Building Performance"
  },
];

function Home() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const slider = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(slider);
  }, []);

  const nextSlide = () => setCurrent((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrent((prev) => (prev - 1 + slides.length) % slides.length);

  // Helper function to reset scroll placement on navigation
  const handlePageScrollReset = () => {
    window.scrollTo({
      top: 0,
      behavior: "instant"
    });
  };

  return (
    <div className="bg-white text-slate-800 antialiased">
      
      {/* HERO CAROUSEL SECTION */}
      <section className="relative h-[95vh] md:h-screen w-full overflow-hidden bg-[#020617]">
        {/* Structural Telemetry Matrix Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:50px_50px] z-20"></div>
        
        {/* Background Slides Cascade */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-[1200ms] ease-in-out ${
              current === index ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-105 pointer-events-none"
            }`}
          >
            {/* Zoom effect on background image */}
            <img
              src={slide.image}
              alt={slide.title}
              className={`w-full h-full object-cover transition-transform duration-[6000ms] ease-out ${
                current === index ? "scale-105" : "scale-110"
              }`}
            />

            {/* Ambient Shading Gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-transparent flex items-center">
              
              {/* Fine Drafting Blueprint Corner Bounds */}
              <div className="absolute top-8 left-8 right-8 bottom-8 border border-white/[0.03] pointer-events-none hidden md:block">
                <span className="absolute top-0 left-0 w-4 h-4 border-t border-l border-white/20"></span>
                <span className="absolute top-0 right-0 w-4 h-4 border-t border-r border-white/20"></span>
                <span className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-white/20"></span>
                <span className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-white/20"></span>
              </div>

              <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                  
                  {/* Main Content Info Block */}
                  <div className="lg:col-span-8 space-y-6">
                    {/* Upper animated tag */}
                    <div className={`inline-flex items-center gap-2 bg-white/[0.04] backdrop-blur-md border border-white/10 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[3px] text-slate-300 shadow-xl transition-all duration-700 delay-300 transform ${
                      current === index ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                    }`}>
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                      {slide.tagline}
                    </div>

                    {/* Main Heading */}
                    <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tight leading-[1.1] transition-all duration-700 delay-500 transform ${
                      current === index ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                    }`}>
                      {slide.title}
                    </h1>

                    {/* Subtitle */}
                    <p className={`text-base md:text-lg text-slate-400 font-light leading-relaxed max-w-2xl transition-all duration-700 delay-700 transform ${
                      current === index ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                    }`}>
                      {slide.subtitle}
                    </p>

                    {/* Actions buttons */}
                    <div className={`flex flex-wrap gap-4 pt-4 transition-all duration-700 delay-900 transform ${
                      current === index ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                    }`}>
                      <Link 
                        to="/contact"
                        onClick={handlePageScrollReset}
                        className="bg-white text-slate-950 px-8 py-4 rounded-xl font-semibold shadow-2xl hover:bg-slate-100 hover:scale-[1.02] transition-all duration-300 flex items-center gap-2.5 group text-sm no-underline"
                      >
                        Get Corporate Estimate
                        <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                      </Link>
                      <Link 
                        to="/services"
                        onClick={handlePageScrollReset}
                        className="border border-white/15 text-white bg-white/[0.02] backdrop-blur-md px-8 py-4 rounded-xl font-medium text-sm hover:bg-white/10 hover:border-white/30 transition-all duration-300 shadow-inner no-underline"
                      >
                        Explore Capabilities
                      </Link>
                    </div>
                  </div>

                  {/* Right Floating Analytics Box */}
                  <div className={`hidden lg:block lg:col-span-4 transition-all duration-1000 delay-700 transform ${
                    current === index ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"
                  }`}>
                    <div className="bg-slate-900/40 backdrop-blur-xl border border-white/[0.06] p-8 rounded-3xl shadow-2xl space-y-6 relative overflow-hidden">
                      <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-slate-400 to-transparent opacity-50"></div>
                      <div className="flex justify-between items-start">
                        <span className="text-[10px] font-mono font-bold uppercase tracking-[2px] text-slate-500">Engineering Telemetry</span>
                        <span className="text-xs font-mono font-bold text-emerald-400 px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20">ACTIVE</span>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-white/[0.04] rounded-xl border border-white/10 text-slate-400"><FaCubes /></div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Engineering Partners</p>
                            <p className="text-sm font-bold text-slate-200">Falcosys & Astra Engineering</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-white/[0.04] rounded-xl border border-white/10 text-slate-400"><FaShieldAlt /></div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Compliance Matrix</p>
                            <p className="text-sm font-bold text-slate-200">Code-Compliant Shop Drawings</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-white/[0.04] rounded-xl border border-white/10 text-slate-400"><FaChartLine /></div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Performance Goal</p>
                            <p className="text-sm font-bold text-white tracking-wide">{slide.metric}</p>
                          </div>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-white/[0.05] flex justify-between items-center text-[10px] font-mono text-slate-500">
                        <span>SYS ID: VAMCO.SYN</span>
                        <span>LOC: DEIRA CLOCK TOWER</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        ))}

        {/* Minimalist Controller Box */}
        <div className="absolute bottom-12 right-12 hidden sm:flex gap-3 z-30">
          <button 
            onClick={prevSlide}
            className="w-12 h-12 rounded-xl border border-white/10 bg-slate-900/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-white hover:text-slate-950 hover:border-white transition-all duration-300 group shadow-lg"
          >
            <FaChevronLeft className="text-xs group-hover:-translate-x-0.5 transition-transform" />
          </button>
          <button 
            onClick={nextSlide}
            className="w-12 h-12 rounded-xl border border-white/10 bg-slate-900/20 backdrop-blur-md text-white flex items-center justify-center hover:bg-white hover:text-slate-950 hover:border-white transition-all duration-300 group shadow-lg"
          >
            <FaChevronRight className="text-xs group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* Linear Progress Indicators */}
        <div className="absolute bottom-12 left-6 right-6 sm:right-auto sm:left-12 flex gap-4 z-30">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              className="flex-1 sm:flex-initial text-left group focus:outline-none"
            >
              <div className="w-full sm:w-24 h-[3px] bg-white/10 rounded-full overflow-hidden transition-all duration-300">
                <div 
                  className={`h-full bg-white transition-all duration-300 ${
                    current === index ? "w-full" : "w-0 group-hover:w-1/3"
                  }`}
                  style={{ transitionDuration: current === index ? "6000ms" : "300ms" }}
                />
              </div>
              <span className={`text-[10px] font-mono font-bold tracking-widest mt-2.5 block transition-all duration-300 ${
                current === index ? "text-white" : "text-slate-600 group-hover:text-slate-400"
              }`}>
                0{index + 1}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ABOUT VAMCO SECTION */}
      <section className="py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-[4px] text-slate-400 block">
                About Vamco & Associates
              </span>
              <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                Multi-Disciplinary <br />Engineering 
              </h2>
              <div className="w-12 h-1 bg-slate-900 rounded"></div>
              <p className="text-slate-600 leading-relaxed text-base font-light">
                Vamco Architectural Prospective Drawings Services LLC, together with our trusted associates Falcosys Infotech Ltd and Astra Engineering Consultants, forms a premier multi-disciplinary engineering . By combining our specialized expertise, we deliver high-quality engineering solutions that support efficient, accurate, and cost-effective project execution.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-3 bg-slate-100 rounded-3xl -rotate-1 pointer-events-none"></div>
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5"
                alt="Engineering drafting layout preview"
                className="relative rounded-3xl shadow-xl object-cover w-full h-[380px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CORPORATE STATS SECTION */}
      <section className="py-20 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.015] pointer-events-none bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:30px_30px]"></div>
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-1">
              <h3 className="text-4xl md:text-5xl font-black text-white">100+</h3>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">Projects Completed</p>
            </div>
            <div className="space-y-1">
              <h3 className="text-4xl md:text-5xl font-black text-white">Engineering</h3>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">Falcosys & Astra</p>
            </div>
            <div className="space-y-1">
              <h3 className="text-4xl md:text-5xl font-black text-white">GCC</h3>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">And Global Coverage</p>
            </div>
            <div className="space-y-1">
              <h3 className="text-4xl md:text-5xl font-black text-white">BIM</h3>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">MEP Coordination</p>
            </div>
          </div>
        </div>
      </section>

      {/* CORE SERVICES SECTION */}
      <section className="py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-xs uppercase tracking-[4px] text-slate-400 font-bold block">Capabilities</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              Our Core Services
            </h2>
            <div className="w-12 h-1 bg-slate-900 mx-auto rounded"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200/50 hover:shadow-md transition duration-300 space-y-3">
              <h3 className="font-bold text-xl text-slate-900">
                Structural Rebar Detailing
              </h3>
              <p className="text-slate-500 text-sm font-light leading-relaxed">
                Producing precise reinforcement detailing that meets rigorous project structural layout specifications and design guidelines seamlessly.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200/50 hover:shadow-md transition duration-300 space-y-3">
              <h3 className="font-bold text-xl text-slate-900">
                Optimized Schedules (BBS)
              </h3>
              <p className="text-slate-500 text-sm font-light leading-relaxed">
                Developing accurate and optimized bending metrics that directly minimize steel scrap, reduce baseline material costs, and accelerate placement.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200/50 hover:shadow-md transition duration-300 space-y-3">
              <h3 className="font-bold text-xl text-slate-900">
                MEP BIM Coordination
              </h3>
              <p className="text-slate-500 text-sm font-light leading-relaxed">
                Creating coordinated Building Information Models integrating Mechanical, Electrical, and Plumbing with structural items into a clash-free system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US SECTION */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-xs uppercase tracking-[4px] text-slate-400 font-bold block">Value Proposition</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              Why Choose VAMCO & Associates
            </h2>
            <div className="w-12 h-1 bg-slate-900 mx-auto rounded"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="border border-slate-200 rounded-2xl p-8 bg-slate-50/50 space-y-3">
              <h3 className="font-bold text-lg text-slate-900">Clash-Free Models</h3>
              <p className="text-slate-500 text-sm font-light leading-relaxed">Advanced coordination workflows engineered to bridge design layout gaps, ensuring structural integrity and flawless building performance.</p>
            </div>

            <div className="border border-slate-200 rounded-2xl p-8 bg-slate-50/50 space-y-3">
              <h3 className="font-bold text-lg text-slate-900">Minimized Steel Scrap</h3>
              <p className="text-slate-500 text-sm font-light leading-relaxed">Production-ready Bar Bending Schedules engineered directly to reduce reinforcement material waste modifications and project overheads.</p>
            </div>

            <div className="border border-slate-200 rounded-2xl p-8 bg-slate-50/50 space-y-3">
              <h3 className="font-bold text-lg text-slate-900">Engineering Expertise</h3>
              <p className="text-slate-500 text-sm font-light leading-relaxed">Combining specialized resources from VAMCO LLC, Falcosys Infotech, and Astra Consultants to ensure reliable engineering execution globally.</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Home;